import { useEffect, useMemo, useRef } from 'react'
import {
  ArrayInput,
  SimpleFormIterator,
  TextInput,
  NumberInput,
  FormDataConsumer,
  useNotify,
  SelectArrayInput,
  useSimpleFormIteratorItem,
} from 'react-admin'
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Chip,
} from '@mui/material'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DeleteIcon from '@mui/icons-material/Delete'
import { useFormContext, useWatch } from 'react-hook-form'

// Episode selection replaced with SelectArrayInput for multi-select UX

// Attaches an explicit { id, order } per episode so the server doesn't have
// to infer order from array position alone. Passes non-arrays through
// untouched (e.g. undefined means "groups weren't part of this submit" and
// must not be turned into an empty array, or setGroups.ts would wipe them).
export const attachEpisodeOrder = groups =>
  Array.isArray(groups)
    ? groups.map(group => ({
        ...group,
        episodes: Array.isArray(group.episodeIds)
          ? group.episodeIds.map((id, index) => ({ id, order: index + 1 }))
          : [],
      }))
    : groups

const SelectedEpisodesManager = ({
  selectedIds = [],
  episodes,
  onReorder,
  onRemove,
  playlistIndex = 0,
}) => {
  const episodesMap = useMemo(() => {
    const map = {}
    episodes?.forEach(ep => {
      if (ep && ep.id) map[ep.id] = ep
    })
    return map
  }, [episodes])

  const moveUp = index => {
    if (index <= 0) return
    const items = Array.from(selectedIds)
    ;[items[index - 1], items[index]] = [items[index], items[index - 1]]
    onReorder(items)
  }

  const moveDown = index => {
    if (index >= selectedIds.length - 1) return
    const items = Array.from(selectedIds)
    ;[items[index + 1], items[index]] = [items[index], items[index + 1]]
    onReorder(items)
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Selected Episodes & Order
      </Typography>
      <Droppable droppableId={`selected-episodes-${playlistIndex}`}>
        {provided => (
          <List
            dense
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              width: '100%',
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 1,
            }}>
            {selectedIds.map((id, index) => {
              const ep = episodesMap[id]
              return (
                <Draggable
                  key={`${playlistIndex}-${id}`}
                  draggableId={`${playlistIndex}-${id}`}
                  index={index}>
                  {draggableProvided => (
                    <ListItem
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                      secondaryAction={
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => moveUp(index)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveDown(index)}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemove(id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      }>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={ep?.title || `Episode ${ep?.order ?? '?'}`}
                        secondary={ep ? `Order: ${ep.order}` : id}
                      />
                    </ListItem>
                  )}
                </Draggable>
              )
            })}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </Box>
  )
}

const GroupItem = ({
  index,
  scopedFormData,
  moveUp,
  moveDown,
  totalGroups,
  sortedGroups,
}) => {
  const notify = useNotify()
  const { setValue } = useFormContext()
  const episodes = useWatch({ name: 'episodes' })
  const groups = useWatch({ name: 'groups' }) || []

  // Use the actual index from the iterator, fallback to 0 only if truly unavailable
  const actualIndex = typeof index === 'number' ? index : 0
  const episodeIdsPath = `groups.${actualIndex}.episodeIds`
  const selectedIds = useWatch({ name: episodeIdsPath }) || []

  // Always prioritize the watched selectedIds over scopedFormData for consistency
  const actualSelectedIds =
    selectedIds.length > 0 ? selectedIds : scopedFormData?.episodeIds || []

  const episodeChoices = useMemo(() => {
    return (episodes || [])
      .filter(ep => ep && ep.id)
      .map(ep => ({ id: ep.id, title: ep.title, order: ep.order }))
  }, [episodes])

  const reorderEpisodes = nextOrder => {
    setValue(episodeIdsPath, nextOrder, {
      shouldDirty: true,
      shouldValidate: true,
    })
    // Force a re-render by triggering form validation
    setTimeout(() => {
      notify(`Group ${actualIndex + 1} episodes reordered`, { type: 'info' })
    }, 100)
  }

  const removeEpisode = id => {
    const next = (actualSelectedIds || []).filter(i => i !== id)
    setValue(episodeIdsPath, next, { shouldDirty: true, shouldValidate: true })
    setTimeout(() => {
      notify(`Episode removed from group ${actualIndex + 1}`, {
        type: 'info',
      })
    }, 100)
  }

  const handleDragEnd = result => {
    if (!result.destination) return

    const items = Array.from(actualSelectedIds)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    reorderEpisodes(items)
  }

  // Get the current group's order and position
  const currentGroup = groups[actualIndex]
  const groupOrder = currentGroup?.order ?? actualIndex + 1
  const currentSortedIndex =
    sortedGroups?.findIndex(g => g.originalIndex === actualIndex) ?? -1
  const isFirst = currentSortedIndex === 0
  const isLast = currentSortedIndex === (sortedGroups?.length ?? 1) - 1

  return (
    <Draggable
      draggableId={`group-${actualIndex}`}
      index={actualIndex}
      key={`group-${actualIndex}`}>
      {(provided, snapshot) => (
        <Card
          variant="outlined"
          sx={{
            mb: 1,
            mt: 1,
            width: '100%',
            backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'white',
          }}
          ref={provided.innerRef}
          {...provided.draggableProps}>
          <CardHeader
            title={`Group #${groupOrder}`}
            action={
              <Stack direction="row" spacing={1}>
                <IconButton
                  size="small"
                  onClick={() => moveUp && moveUp(actualIndex)}
                  disabled={isFirst}
                  {...provided.dragHandleProps}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveDown && moveDown(actualIndex)}
                  disabled={isLast}
                  {...provided.dragHandleProps}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <NumberInput
                  source={'order'}
                  label="Order"
                  fullWidth
                  variant="standard"
                  disabled
                  sx={{ display: 'none' }}
                />
                <TextInput
                  source={'name'}
                  label="Name"
                  fullWidth
                  variant="standard"
                />
                <TextInput
                  source={'description'}
                  label="Description"
                  fullWidth
                  variant="standard"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Episodes
                </Typography>
                {episodeChoices?.length ? (
                  <SelectArrayInput
                    source={'episodeIds'}
                    label=""
                    fullWidth
                    variant="standard"
                    choices={episodeChoices}
                    optionText={choice =>
                      choice.title || `Episode ${choice.order ?? ''}`
                    }
                    optionValue="id"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No episodes available yet. Create episodes in Course Details
                    tab.
                  </Typography>
                )}
                <Box sx={{ mt: 2 }}>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <SelectedEpisodesManager
                      selectedIds={actualSelectedIds}
                      episodes={episodes}
                      onReorder={reorderEpisodes}
                      onRemove={removeEpisode}
                      playlistIndex={actualIndex}
                    />
                  </DragDropContext>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Draggable>
  )
}

const CourseGroupTab = () => {
  const notify = useNotify()
  const { setValue } = useFormContext()
  const groups = useWatch({ name: 'groups' }) || []
  const prevLenRef = useRef(groups.length)

  useEffect(() => {
    const curr = groups.length
    const prev = prevLenRef.current
    if (curr > prev) {
      notify('Group created', { type: 'info' })
    } else if (curr < prev) {
      notify('Group deleted', { type: 'warning' })
    }
    prevLenRef.current = curr
  }, [groups, notify])

  // Sort groups by order field
  const sortedGroups = useMemo(() => {
    if (!groups || groups.length === 0) return []
    return [...groups]
      .map((group, index) => ({ ...group, originalIndex: index }))
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
  }, [groups])

  const moveGroup = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return

    const updatedGroups = [...groups]
    const [movedGroup] = updatedGroups.splice(fromIndex, 1)
    updatedGroups.splice(toIndex, 0, movedGroup)

    // Update order field for all groups
    const groupsWithNewOrder = updatedGroups.map((group, index) => ({
      ...group,
      order: index + 1,
    }))

    setValue('groups', groupsWithNewOrder, {
      shouldDirty: true,
      shouldValidate: true,
    })

    notify('Group order updated', { type: 'info' })
  }

  const handleDragEnd = result => {
    if (!result.destination) return
    moveGroup(result.source.index, result.destination.index)
  }

  const moveUp = originalIndex => {
    const currentIndex = sortedGroups.findIndex(
      g => g.originalIndex === originalIndex,
    )
    if (currentIndex <= 0) return
    const prevGroup = sortedGroups[currentIndex - 1]
    moveGroup(originalIndex, prevGroup.originalIndex)
  }

  const moveDown = originalIndex => {
    const currentIndex = sortedGroups.findIndex(
      g => g.originalIndex === originalIndex,
    )
    if (currentIndex >= sortedGroups.length - 1) return
    const nextGroup = sortedGroups[currentIndex + 1]
    moveGroup(originalIndex, nextGroup.originalIndex)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="groups-list">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <ArrayInput source="groups" fullWidth sx={{ width: '100%' }}>
                <SimpleFormIterator
                  getItemLabel={index => {
                    const group = groups[index]
                    const order = group?.order ?? index + 1
                    return `Group ${order}`
                  }}
                  disableReordering={true}>
                  <PlaylistItemWrapper
                    moveUp={moveUp}
                    moveDown={moveDown}
                    totalGroups={groups.length}
                    sortedGroups={sortedGroups}
                  />
                </SimpleFormIterator>
              </ArrayInput>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  )
}

const PlaylistItemWrapper = ({
  moveUp,
  moveDown,
  totalGroups,
  sortedGroups,
}) => {
  const { index } = useSimpleFormIteratorItem()

  return (
    <FormDataConsumer>
      {({ scopedFormData }) => {
        return (
          <GroupItem
            index={index}
            scopedFormData={scopedFormData}
            moveUp={moveUp}
            moveDown={moveDown}
            totalGroups={totalGroups}
            sortedGroups={sortedGroups}
          />
        )
      }}
    </FormDataConsumer>
  )
}

export default CourseGroupTab
